import { gunzipSync, strFromU8 } from "fflate";

import type { AliasDto } from "@models/data/aliasData";
import type { EntitySnapshotDto } from "@models/data/entitySnapshotDto";
import { toNameFloatMap, type NameFloatMap } from "@models/data/nameFloatMap";
import type { PartDto } from "@models/data/partDto";

type JsonRecord = Record<string, unknown>;

/**
 * Decode the payload format used by Unity's V2 `StorageModel`: UTF-8 JSON,
 * normally wrapped in gzip (`EncodeCompressedJsonPayload`), with plain JSON
 * tolerated for compatibility with early data.
 */
export function decodeProjectStorageJson(bytes: Uint8Array): unknown {
  const payload =
    bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b
      ? gunzipSync(bytes)
      : bytes;
  return JSON.parse(strFromU8(payload)) as unknown;
}

/** Convert Unity JsonUtility's lower-camel `PartsContainer` into Dynamo-shaped DTOs. */
export function partsFromV2Payload(payload: unknown): ReadonlyArray<PartDto> {
  const container = asRecord(payload);
  const rows = container ? (container.parts ?? container.Parts) : null;
  if (!Array.isArray(rows)) return [];

  const parts: PartDto[] = [];
  for (const value of rows) {
    const row = asRecord(value);
    const id = readString(row?.id ?? row?.ID);
    if (!row || !id) continue;

    parts.push({
      ...row,
      ID: id,
      Name: readString(row.name ?? row.Name),
      DisplayName: readString(row.displayName ?? row.DisplayName),
      Description: readString(row.description ?? row.Description),
      HierachyID: readString(
        row.hierarchyID
        ?? row.hierachyID
        ?? row.HierarchyID
        ?? row.HierachyID,
      ),
      Position: vecFromJson(row.position ?? row.Position),
      Rotation: vecFromJson(row.rotation ?? row.Rotation),
      Scale: vecFromJson(row.scale ?? row.Scale),
      MaterialIDs: stringArray(row.materialIDs ?? row.MaterialIDs),
      Visibility: readBoolean(row.visibility ?? row.Visibility),
      Merged: readBoolean(row.merged ?? row.Merged),
      Aliases: aliasesFromJson(row.aliases ?? row.Aliases),
    });
  }
  return parts;
}

/**
 * Convert Unity JsonUtility's lower-camel `EntitySnapshotsContainer` into the
 * existing Dynamo-shaped DTO contract consumed by `mapEntities`.
 */
export function snapshotsFromV2Payload(
  payload: unknown,
): ReadonlyArray<EntitySnapshotDto> {
  const container = asRecord(payload);
  const rows = container
    ? (container.entitySnapshots ?? container.EntitySnapshots)
    : null;
  if (!Array.isArray(rows)) return [];

  const snapshots: EntitySnapshotDto[] = [];
  for (const value of rows) {
    const row = asRecord(value);
    const entityId = readString(row?.entityID ?? row?.EntityID);
    const snapshotId = readString(row?.snapshotID ?? row?.SnapshotID);
    if (!row || !entityId || !snapshotId) continue;

    snapshots.push({
      ...row,
      SnapshotID: snapshotId,
      EntityID: entityId,
      PartID: readString(row.partID ?? row.PartID),
      Name: readString(row.name ?? row.Name),
      DisplayName: readString(row.displayName ?? row.DisplayName),
      IsDeleted: readBoolean(row.isDeleted ?? row.IsDeleted),
      IsRoot: readBoolean(row.isRoot ?? row.IsRoot),
      IsLock: readBoolean(row.isLock ?? row.IsLock),
      Visibility: readBoolean(row.visibility ?? row.Visibility),
      ForcedVisibility: readBoolean(
        row.forcedVisibility ?? row.ForcedVisibility,
      ),
      HasMergedChildren: readBoolean(
        row.merged
        ?? row.hasMergedChildren
        ?? row.Merged
        ?? row.HasMergedChildren,
      ),
      IsCollisionEnabled: readBoolean(
        row.isCollisionEnabled ?? row.IsCollisionEnabled,
      ),
      IsGravityEnabled: readBoolean(
        row.isGravityEnabled ?? row.IsGravityEnabled,
      ),
      ExplosionFactor: readNumber(row.explosionFactor ?? row.ExplosionFactor),
      MaterialIDs: stringArray(row.materialIDs ?? row.MaterialIDs),
      PersistentSpinJson: readString(
        row.persistentSpinJson ?? row.PersistentSpinJson,
      ),
      PersistentHighlightJson: readString(
        row.persistentHighlightJson ?? row.PersistentHighlightJson,
      ),
      Aliases: aliasesFromJson(row.aliases ?? row.Aliases),
      Position: vecFromJson(row.position ?? row.Position),
      Rotation: vecFromJson(row.rotation ?? row.Rotation),
      Scale: vecFromJson(row.scale ?? row.Scale),
      AuthoringPos: vecFromJson(row.authoringPos ?? row.AuthoringPos),
      AuthoringRot: vecFromJson(row.authoringRot ?? row.AuthoringRot),
      InitialPos: vecFromJson(row.initialPos ?? row.InitialPos),
      // `intialRot` is misspelled in Unity's serialized V2 payload.
      InitialRot: vecFromJson(
        row.intialRot
        ?? row.initialRot
        ?? row.IntialRot
        ?? row.InitialRot,
      ),
      Zoom: readNumber(row.zoom ?? row.Zoom),
    });
  }
  return snapshots;
}

/**
 * Dynamo V2 rows are overrides. Unity writes full rows, while the web's
 * `UpdateItem` path can create sparse patch rows. Derive missing snapshot ids
 * from the sort key so both forms can participate in the merge.
 */
export function normalizeSnapshotOverrides(
  rows: ReadonlyArray<EntitySnapshotDto>,
): ReadonlyArray<EntitySnapshotDto> {
  const normalized: EntitySnapshotDto[] = [];
  for (const row of rows) {
    const snapshotId =
      readString(row.SnapshotID)
      ?? snapshotIdFromSortKey(readString(row.sk));
    if (!snapshotId) continue;
    normalized.push({
      ...row,
      SnapshotID: snapshotId,
      HasMergedChildren:
        readBoolean(row.HasMergedChildren ?? row.Merged)
        ?? row.HasMergedChildren,
    });
  }
  return normalized;
}

/**
 * Port of Unity `StorageModel.MergeEntitySnapshots`. Full Unity override rows
 * replace their base row; sparse web patch rows overlay it so unchanged
 * identity and hierarchy fields are retained.
 */
export function mergeEntitySnapshots(
  baseSnapshots: ReadonlyArray<EntitySnapshotDto>,
  overrides: ReadonlyArray<EntitySnapshotDto>,
): ReadonlyArray<EntitySnapshotDto> {
  const byId = new Map<string, EntitySnapshotDto>();
  const orderedIds: string[] = [];

  for (const snapshot of baseSnapshots) {
    const id = snapshotId(snapshot);
    if (!id || snapshot.IsDeleted === true) continue;
    if (!byId.has(id)) orderedIds.push(id);
    byId.set(id, snapshot);
  }

  for (const override of normalizeSnapshotOverrides(overrides)) {
    const id = snapshotId(override);
    if (!id) continue;
    if (override.IsDeleted === true) {
      byId.delete(id);
      continue;
    }

    if (!byId.has(id)) orderedIds.push(id);
    const base = byId.get(id);
    const isFullRow =
      typeof override.EntityID === "string"
      && override.EntityID.length > 0
      && typeof override.PartID === "string"
      && override.PartID.length > 0;
    byId.set(
      id,
      base && !isFullRow
        ? { ...base, ...override, SnapshotID: id }
        : override,
    );
  }

  return orderedIds.flatMap((id) => {
    const snapshot = byId.get(id);
    return snapshot ? [snapshot] : [];
  });
}

export function filterDeletedSnapshots(
  snapshots: ReadonlyArray<EntitySnapshotDto>,
): ReadonlyArray<EntitySnapshotDto> {
  return normalizeSnapshotOverrides(snapshots).filter(
    (snapshot) => snapshot.IsDeleted !== true,
  );
}

function snapshotId(snapshot: EntitySnapshotDto): string | undefined {
  return readString(snapshot.SnapshotID) ?? readString(snapshot.PartID);
}

function snapshotIdFromSortKey(sk: string | undefined): string | undefined {
  if (!sk) return undefined;
  const marker = ":snapshot:";
  const index = sk.lastIndexOf(marker);
  return index >= 0 ? readString(sk.slice(index + marker.length)) : undefined;
}

function aliasesFromJson(value: unknown): ReadonlyArray<AliasDto> | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.flatMap((entry) => {
    const row = asRecord(entry);
    if (!row) return [];
    const aliasValue = readString(row.value ?? row.Value);
    if (!aliasValue) return [];
    return [{
      ...row,
      Language: readString(row.language ?? row.Language) ?? undefined,
      Value: aliasValue,
    }];
  });
}

function stringArray(value: unknown): ReadonlyArray<string> | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is string => typeof item === "string");
}

/**
 * A V2 vector reaches us as JsonUtility's `NameFloatPair[]` —
 * `[{name:"x",val:1}, …]` — where the Dynamo-shaped DTO contract every
 * consumer was written against is the `{x, y, z}` map `unmarshall` produces
 * for an `M` attribute. Fold it at THIS boundary rather than trusting each
 * reader to: the read side mostly did (`toNameFloatMap` accepts both), but a
 * WRITER that copies the DTO back out (`placeLibraryAssetAsync` → the Dynamo
 * `vecAttribute` marshaller) indexed `.x` on the array, got `undefined`, and
 * persisted `{0,0,0}` for every axis of every child part. The placed model
 * rendered correctly from memory and collapsed to a zero-scale point on the
 * next reload — the "library model sometimes does not appear" report
 * (2026-09-03), which was specific to assets saved by a V2 desktop client.
 *
 * An ABSENT vector stays `undefined` (not `{}`), so a downstream
 * `?? default` still sees the absence.
 */
function vecFromJson(value: unknown): NameFloatMap | undefined {
  if (value === null || value === undefined) return undefined;
  const map = toNameFloatMap(value);
  return Object.keys(map).length > 0 ? map : undefined;
}

function asRecord(value: unknown): JsonRecord | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as JsonRecord
    : null;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}
